import { CustomFieldPayload, NoteFieldGroup } from 'services/lead-meeting-internal-note'
import { CustomFieldAccessor } from 'types/global'
import { Permissions } from 'services/types/user'

import { formatDatetime } from './datetime'
import { AccessType, DndError, FIELD_TYPE_ACCESSOR } from './constants'

export const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })

export const FORMAT_NO_NAME = '(No name)'
export const NO_NAME = 'No name'

export const formatContactNumberDisplayValue = (prefix?: number, number?: number | string) =>
  prefix && number ? `(+${prefix}) ${number}` : ''

export const formatContactNumber = (prefix?: number, number?: number | string) =>
  prefix && number ? `+${prefix}${number}` : ''

export const formatName = (fname: string | undefined, lname: string | undefined) =>
  ((fname || lname) && `${fname ?? ''} ${lname ?? ''}`) || FORMAT_NO_NAME

/**
 * Added to format datepicker from `new Date()` format to yyyy-MM-dd
 * Can add other necessary formatting when needed
 */
export const formatCustomFieldPayload = (
  formValue: CustomFieldPayload,
  ref: NoteFieldGroup[] | undefined,
): CustomFieldPayload => {
  let customFieldAccessor: CustomFieldAccessor | undefined

  ref?.forEach((o) => {
    if (!customFieldAccessor) {
      customFieldAccessor = o.custom_fields.find((i) => i.id === formValue.custom_field_id)?.accessor
    }
  })

  if (!customFieldAccessor) {
    return formValue
  }

  switch (customFieldAccessor) {
    case FIELD_TYPE_ACCESSOR.datePicker: {
      if (typeof formValue.value === 'string') {
        return {
          ...formValue,
          value: formatDatetime(formValue.value, 'yyyy-MM-dd') ?? '',
        }
      }
      return formValue
    }
    default:
      return formValue
  }
}

export type UserInfo = {
  id: string
  teamIds: string[] | null
}

export type LeadOwner = {
  leadOwnerId: string | null
  leadOwnerTeamId: string | null
}

/**
 * Identify user as lead custodian (isOwner) if:
 *  a.) User is either assigned as lead owner or a member of the team set as lead owner
 *  b.) User is a collaborator of the lead
 *  c.) Lead is queued for user via Next Lead queuing logic
 *  d.) User will be custodian if canEditOtherAccount permission is enabled
 * @param userInfo
 * @param leadOwner
 * @param collaborators
 * @param isQueuedForUser
 * @returns boolean
 */
export const isLeadCustodian = (
  userInfo: UserInfo,
  leadOwner: LeadOwner,
  collaborators: string[] | null,
  isQueuedForUser = false,
  currentUsersTeamMembers: string[] | null = [],
  permissions: Permissions['accountWorkflow'],
): boolean =>
  permissions?.canEditOtherAccount ||
  userInfo.id === leadOwner.leadOwnerId ||
  (userInfo.teamIds || []).includes(leadOwner.leadOwnerTeamId || '') ||
  collaborators?.includes(userInfo.id) ||
  (currentUsersTeamMembers?.includes(leadOwner.leadOwnerId || '') &&
    (permissions?.canEditOtherAccount || permissions?.canEditTeam)) ||
  isQueuedForUser

export const isMyTeamMembersIsCollaboratorInLead = ({
  permissions,
  currentUsersTeamMembers,
  collaborators,
}: {
  permissions: Permissions['accountWorkflow']
  currentUsersTeamMembers?: string[] | null
  collaborators: string[] | null
}) => {
  let isTeamMembersInCollab = false

  if (currentUsersTeamMembers?.some((ct) => collaborators?.includes(ct))) {
    isTeamMembersInCollab = true
  }

  return isTeamMembersInCollab && (permissions?.canEditTeam || permissions?.canEditOtherAccount)
}

/**
 * Validate access based on lead ownership and user permissions
 * @param userInfo
 * @param leadOwner
 * @param collaborators
 * @param permissions
 * @param accessType
 * @param isQueuedForUser
 * @returns boolean
 */
export const isLeadAccessible = (
  userInfo: UserInfo,
  leadOwner: LeadOwner,
  collaborators: string[] | null,
  permissions: Permissions['accountWorkflow'],
  currentUsersTeamMembers: string[] | null,
  accessType: keyof typeof AccessType = 'view',
  isQueuedForUser = false,
): boolean => {
  const teamMembersWithoutLoggedInUser = currentUsersTeamMembers?.filter((i) => i !== userInfo.id)
  const isLeadUnassigned = !leadOwner.leadOwnerId && !leadOwner.leadOwnerTeamId
  const isUserLeadCustodian = isLeadCustodian(
    userInfo,
    leadOwner,
    collaborators,
    isQueuedForUser,
    teamMembersWithoutLoggedInUser,
    permissions,
  )
  let result: boolean | undefined = false

  const isTeamMembersInCollaborator = isMyTeamMembersIsCollaboratorInLead({
    permissions,
    currentUsersTeamMembers: teamMembersWithoutLoggedInUser,
    collaborators,
  })

  switch (accessType) {
    case AccessType.create:
    case AccessType.delete:
      break
    case AccessType.edit:
      if (isLeadUnassigned) {
        result =
          permissions?.canEditOtherAccount ||
          isTeamMembersInCollaborator ||
          permissions?.canEditUnassignedLead ||
          (isUserLeadCustodian && permissions?.canEdit) // even though lead is unassigned logged in user can-
        // -become lead custodian when the logged in user is a collab
      } else {
        // case 1:'canEditOtherAccount' is enough to get access on all leads.
        // case 2: if any of the logged in user's team member is in the collabs-
        // -user only need canEditTeam or canEditOtherAccount permission
        // case 3: if the user is the custodian then canEdit(edit own account) permission is needed
        // case 4: if the lead owner is member of team member then canEditTeam permission is needed.

        result =
          permissions?.canEditOtherAccount ||
          (isTeamMembersInCollaborator === true
            ? isTeamMembersInCollaborator
            : isUserLeadCustodian && permissions?.canEdit) ||
          (teamMembersWithoutLoggedInUser?.includes(leadOwner.leadOwnerId || '') && permissions?.canEditTeam)
      }

      break
    default:
      if (isLeadUnassigned) {
        result = permissions?.canEditOtherAccount || permissions?.canViewUnassignedLead
      } else {
        result =
          permissions?.canEditOtherAccount ||
          (isUserLeadCustodian ? permissions?.canView : permissions?.canViewOtherAccount)
      }
  }

  return Boolean(result)
}

export const resolveDndErrorMessage = (code: string) => {
  if (Object.keys(DndError).includes(code)) {
    return DndError[(code as unknown) as keyof typeof DndError]
  }

  return 'something_went_wrong_error'
}
